import React, { useEffect, useState } from 'react'
import { Card, CardBody, CardTitle, Col, Row, Table } from 'reactstrap'
import ReactECharts from 'echarts-for-react'
import Request from '../../../helpers/request'
import * as url from '../../../helpers/url_helper'
import { fwDisplay } from '../../../helpers/fw'

// Production tab — the FULL funnel (Ram, 2026-08-04): board QC → consumption
// in assembly → products assembled → per-MAC functional tests → field
// deployment. Data from GET /admin/v1/user/production-funnel (READ-ONLY
// backend joins). Data-honesty rules (Ram: "make sure all the data shown is
// correct and clearly indicate where using placeholders"):
//   • 'unset' firmware buckets are labelled "not recorded", never hidden
//   • unrecognised/bench serials are counted in footnotes, never dropped
//   • unmatched test records are surfaced
//   • if the funnel endpoint is absent (older backend), the page says so
//     explicitly instead of rendering empty charts

const TITLE_STYLE = { color: '#5D6679', fontWeight: 500, fontSize: 16 }
const CAPTION = { fontSize: 11, color: '#B8BFC9', marginBottom: 0 } as const
const CARD_STYLE = { borderRadius: 10 }

const ProductionInventory = () => {
    const [funnel, setFunnel] = useState<any>(null)
    const [loaded, setLoaded] = useState(false)
    const [errStatus, setErrStatus] = useState<number | null>(null)

    useEffect(() => {
        Request.get(url.GET_PRODUCTION_FUNNEL).then((r: any) => {
            setFunnel(r?.data?.funnel || null)
            if (!r?.data?.funnel) setErrStatus(-1)  // 200 with unexpected body
            setLoaded(true)
        }).catch((e: any) => {
            setErrStatus(e?.response?.status ?? 0)  // 0 = network/no response
            setLoaded(true)
        })
    }, [])

    const vLabel = (v: string) => v === 'unset' ? 'not recorded' : fwDisplay(v)

    // interval: 0 forces EVERY category label to render (echarts' 'auto'
    // silently skips labels when they would overlap — with the grey
    // 'not recorded' row that would break the placeholder-honesty rule);
    // height grows with the row count for the same reason.
    const miniH = (versions: any[]) => Math.max(120, versions.length * 16 + 30)
    const mkMini = (versions: any[], color: string) => {
        const rows = [...versions].sort((a: any, b: any) => b.count - a.count).reverse()
        return {
            tooltip: {},
            grid: { left: 66, right: 14, top: 4, bottom: 18 },
            xAxis: { type: 'value', axisLabel: { fontSize: 9 }, minInterval: 1 },
            yAxis: { type: 'category', data: rows.map((v: any) => vLabel(v.version)), axisLabel: { fontSize: 9, interval: 0 } },
            series: [{
                type: 'bar', barMaxWidth: 12,
                data: rows.map((v: any) => ({
                    value: v.count,
                    itemStyle: { color: v.version === 'unset' ? '#B8BFC9' : color },
                })),
            }],
        }
    }

    if (!loaded) return <div className="page-content"><p style={{ color: '#858D9D' }}>Loading…</p></div>
    if (!funnel) {
        // Only a real 404 means "older backend" — saying that for a 500 or a
        // network blip would state a false cause.
        const olderBackend = errStatus === 404
        return (
            <div className="page-content">
                <Card style={CARD_STYLE}><CardBody>
                    <CardTitle style={TITLE_STYLE}>Production funnel</CardTitle>
                    <p style={{ fontSize: 13, color: '#858D9D', marginBottom: 0 }}>
                        {olderBackend ? (
                            <>The production-funnel endpoint is not available on this
                            backend version — no data is being hidden; there is simply
                            nothing to show until the backend is updated.</>
                        ) : (
                            <>Couldn't load the production funnel (server or network
                            error{errStatus && errStatus > 0 ? ` — HTTP ${errStatus}` : ''}).
                            The data is fine; reload the page to retry.</>
                        )}
                    </p>
                </CardBody></Card>
            </div>
        )
    }

    const boards = [
        ...(funnel.acs || []).map((a: any) => ({ ...a, label: a.family, kind: 'AC' })),
        ...(funnel.dcs || []).map((d: any) => ({ ...d, label: `${d.product} DC`, kind: 'DC' })),
        ...(funnel.dalink ? [{ ...funnel.dalink, label: 'DaLink PCBA', kind: 'single PCBA', consumed: null }] : []),
    ]
    const products = ['DaFlip', 'DaTwist', 'DaTwistPro']
    const pvd = funnel.producedVsDeployed || []
    const pvdOption = pvd.length ? {
        tooltip: {},
        legend: { data: ['produced', 'field deployed'], textStyle: { fontSize: 10 } },
        grid: { left: 50, right: 16, top: 30, bottom: 24 },
        xAxis: { type: 'category', data: pvd.map((p: any) => p.product), axisLabel: { interval: 0, fontSize: 10 } },
        yAxis: { type: 'value', axisLabel: { fontSize: 9 } },
        series: [
            { name: 'produced', type: 'bar', barMaxWidth: 26, data: pvd.map((p: any) => p.produced), itemStyle: { color: '#4AA3DF' } },
            { name: 'field deployed', type: 'bar', barMaxWidth: 26, data: pvd.map((p: any) => p.deployed), itemStyle: { color: '#2ecc71' } },
        ],
    } : null

    return (
        <div className="page-content">

            {/* ── 1. Board QC + consumption ─────────────────────────────── */}
            <Row>
                <Col xl="8" lg="12">
                    <Card style={CARD_STYLE}>
                        <CardBody>
                            <CardTitle style={TITLE_STYLE}>Board QC &amp; consumption</CardTitle>
                            <Table size="sm" responsive style={{ fontSize: 12 }}>
                                <thead>
                                    <tr>
                                        <th>Board</th><th>Serial prefix</th><th>Total</th>
                                        <th style={{ color: '#2ecc71' }}>QC pass</th>
                                        <th style={{ color: '#e74c3c' }}>QC fail</th>
                                        <th style={{ color: '#B8BFC9' }}>not recorded</th>
                                        <th>Used in assembly</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {boards.map((b: any) => (
                                        <tr key={b.label}>
                                            <td>{b.label} <span style={{ color: '#B8BFC9' }}>({b.kind})</span></td>
                                            <td>{b.prefix}</td>
                                            <td><b>{b.total}</b></td>
                                            <td>{b.pass}</td>
                                            <td>{b.fail}</td>
                                            <td>{b.notRecorded}</td>
                                            <td>{b.consumed === null ? <span style={{ color: '#B8BFC9' }}>n/a — board is the product</span> : b.consumed}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            <p style={CAPTION}>
                                DaTwist &amp; DaTwistPro share one AC PCBA (TAC); DaLink is a single
                                PCBA (no separate AC/DC). Not counted above (unrecognised/bench
                                serials): {funnel.acOther} AC, {funnel.dcOther} DC,
                                {' '}{funnel.dalink?.other ?? 0} relay board(s)
                                {(funnel.consumedACOther || funnel.consumedDCOther) ? (
                                    <> — plus {funnel.consumedACOther ?? 0} AC / {funnel.consumedDCOther ?? 0} DC
                                    unrecognised serial(s) referenced by assembled products</>
                                ) : null}.
                                {(funnel.assembled?.duplicateAcRefs || funnel.assembled?.duplicateDcRefs) ? (
                                    <> {(funnel.assembled.duplicateAcRefs ?? 0) + (funnel.assembled.duplicateDcRefs ?? 0)} product
                                    row(s) re-reference an already-consumed board serial
                                    ({funnel.assembled.duplicateAcRefs ?? 0} AC, {funnel.assembled.duplicateDcRefs ?? 0} DC) —
                                    legacy data-entry errors; "Used in assembly" counts distinct
                                    boards, so it can sit below "Assembled".</>
                                ) : null}
                            </p>
                        </CardBody>
                    </Card>
                </Col>

                {/* ── 2. Assembly & functional tests ─────────────────────── */}
                <Col xl="4" lg="12">
                    <Card style={CARD_STYLE}>
                        <CardBody>
                            <CardTitle style={TITLE_STYLE}>Assembly &amp; functional tests</CardTitle>
                            <Table size="sm" responsive style={{ fontSize: 12 }}>
                                <thead>
                                    <tr>
                                        <th>Product</th><th>Assembled</th><th>Tested</th>
                                        <th style={{ color: '#2ecc71' }}>Test pass</th>
                                        <th style={{ color: '#e74c3c' }}>Test fail</th>
                                        <th style={{ color: '#B8BFC9' }}>not recorded</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.map((p) => (
                                        <tr key={p}>
                                            <td>{p}</td>
                                            <td><b>{funnel.assembled?.byProduct?.[p] ?? 0}</b></td>
                                            <td>{funnel.tested?.byProduct?.[p]?.total ?? 0}</td>
                                            <td>{funnel.tested?.byProduct?.[p]?.pass ?? 0}</td>
                                            <td>{funnel.tested?.byProduct?.[p]?.fail ?? 0}</td>
                                            <td>{funnel.tested?.byProduct?.[p]?.notRecorded ?? 0}</td>
                                        </tr>
                                    ))}
                                    {funnel.tested?.byProduct?.DaLink ? (
                                        <tr key="DaLink">
                                            <td>DaLink</td>
                                            <td><span style={{ color: '#B8BFC9' }}>= boards</span></td>
                                            <td>{funnel.tested.byProduct.DaLink.total}</td>
                                            <td>{funnel.tested.byProduct.DaLink.pass}</td>
                                            <td>{funnel.tested.byProduct.DaLink.fail}</td>
                                            <td>{funnel.tested.byProduct.DaLink.notRecorded}</td>
                                        </tr>
                                    ) : null}
                                </tbody>
                            </Table>
                            <p style={CAPTION}>
                                Assembled total {funnel.assembled?.total} — includes
                                {' '}{funnel.assembled?.unknown} unit(s) built on DC serials that
                                match no scanned DC board (not attributable to a product).
                                {' '}{funnel.tested?.unmatched} test record(s) could not be
                                attributed to a product. "Tested" counts test RUNS, not unique
                                units. Pass/fail at assembly itself is not recorded by EMS —
                                the functional test is the real QC gate. DaLink tests come
                                from the relay-test log, matched to LAD boards by serial or MAC
                                {funnel.tested?.relayTestOther ? (
                                    <> — {funnel.tested.relayTestOther} record(s) match no LAD board (bench/junk)</>
                                ) : null}.
                            </p>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* ── 3. Firmware flashed on boards ──────────────────────────── */}
            <Row>
                <Col xl="12">
                    <Card style={CARD_STYLE}>
                        <CardBody>
                            <CardTitle style={TITLE_STYLE}>Firmware flashed on boards (production)</CardTitle>
                            <Row>
                                {(funnel.dcs || []).map((d: any) => (
                                    <Col xl="3" md="6" key={d.prefix}>
                                        <div style={{ fontSize: 11, color: '#858D9D', marginBottom: 1 }}>{d.product} DC · {d.total}</div>
                                        <ReactECharts option={mkMini(d.versions, '#4AA3DF')} style={{ height: miniH(d.versions) }} />
                                    </Col>
                                ))}
                                {funnel.dalink ? (
                                    <Col xl="3" md="6">
                                        <div style={{ fontSize: 11, color: '#858D9D', marginBottom: 1 }}>DaLink PCBA · {funnel.dalink.total}</div>
                                        <ReactECharts option={mkMini(funnel.dalink.versions, '#58B19F')} style={{ height: miniH(funnel.dalink.versions) }} />
                                    </Col>
                                ) : null}
                            </Row>
                            <p style={CAPTION}>
                                FW stored in the EMS dotless form, displayed as semver.
                                Grey "not recorded" bars are boards whose flashed version was
                                never captured — a data gap, not a firmware state.
                            </p>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* ── 4. Products by firmware + produced vs deployed ─────────── */}
            <Row>
                <Col xl="8" lg="12">
                    <Card style={CARD_STYLE}>
                        <CardBody>
                            <CardTitle style={TITLE_STYLE}>Products by firmware (from the consumed DC)</CardTitle>
                            <Row>
                                {products.map((p) => (
                                    <Col xl="4" md="6" key={p}>
                                        <div style={{ fontSize: 11, color: '#858D9D', marginBottom: 1 }}>
                                            {p} · {funnel.assembled?.byProduct?.[p] ?? 0}
                                        </div>
                                        <ReactECharts option={mkMini(funnel.productFw?.[p] || [], '#7A6FBE')} style={{ height: miniH(funnel.productFw?.[p] || []) }} />
                                    </Col>
                                ))}
                            </Row>
                            <p style={CAPTION}>
                                A product's firmware is the version flashed on its consumed DC
                                board at production time — the FIELD firmware may differ after
                                OTA. Grey bars = version not recorded at flashing.
                            </p>
                        </CardBody>
                    </Card>
                </Col>
                <Col xl="4" lg="12">
                    <Card style={CARD_STYLE}>
                        <CardBody>
                            <CardTitle style={TITLE_STYLE}>Produced vs field deployed</CardTitle>
                            {pvdOption ? <ReactECharts option={pvdOption} style={{ height: 300 }} /> : null}
                            <p style={CAPTION}>
                                Deployed = product-type module rows in the field DB (all
                                states, including offline)
                                {funnel.deployedOther ? (
                                    <>; {funnel.deployedOther} retired/placeholder row(s) excluded</>
                                ) : null}.
                                Produced counts everything made — including QC fails.
                                DaLink "produced" = LAD boards, since the board is the product.
                            </p>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    )
}

export default ProductionInventory
